import lodash from "lodash";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { GroupTypeFilterValues } from "../../components/group/group-filter/group-type-filter.component";
import { IPaginationMeta } from "../../domain/pagination/pagination-meta";
import { UserConverter } from "../../domain/converter/user.converter";
import { IUserTableRow } from "../../domain/user/user-table-row";
import { IUsersFilter, useGetUsersWithFiltersQuery } from "../../repositories/queries/user/get-users.query";
import { useUsersFilterContext } from "./users-filter-provider";

interface IUsersContextType {
  isLoading: boolean;
  tablePagination: IPaginationMeta;
  setTablePagination: (pagination: IPaginationMeta) => void;
  users: IUserTableRow[];
  filters?: IUsersFilter;
  setFilters: (filters: IUsersFilter) => void;
  isFilterDirty: boolean;
  resetFilter: () => void;
}

interface IUsersProps {
  children: React.ReactNode;
  groupTypes?: GroupTypeFilterValues[];
}

const UsersContext = createContext<IUsersContextType>({} as IUsersContextType);

export const UsersProvider = (props: IUsersProps) => {
  const value = useUsersProvider(props);
  return <UsersContext.Provider value={value}>{props.children}</UsersContext.Provider>;
};

export const useUsersContext = () => {
  return useContext(UsersContext);
};

const useUsersProvider = (props: IUsersProps): IUsersContextType => {
  const { filters, setFilters } = useUsersFilterContext();

  const [tablePagination, setTablePagination] = useState<IPaginationMeta>({
    numberOfRecords: 0,
    pageSize: 50,
    page: 0,
    numberOfPages: 0,
  });

  const { data, isLoading } = useGetUsersWithFiltersQuery(filters, true, tablePagination);

  useEffect(() => {
    const numberOfRecords = data?.meta?.numberOfRecords;
    if (numberOfRecords !== undefined) {
      setTablePagination((prev) => {
        if (prev.numberOfRecords === numberOfRecords) return prev;
        return { ...prev, numberOfRecords };
      });
    }
  }, [data]);

  const isFilterDirty = useMemo(
    () => !lodash.isEmpty(filters.active) || !lodash.isEmpty(filters.emailQuery) || !lodash.isEmpty(filters.lastNameQuery),
    [filters],
  );

  const users = useMemo(
    () => data?.users.map((user, index) => UserConverter.toTableRow(user, index)) ?? [],
    [data],
  );

  const resetFilter = () =>
    setFilters({
      active: undefined,
      emailQuery: undefined,
      lastNameQuery: undefined,
      groupTypes: props.groupTypes,
    });

  return {
    isLoading,
    tablePagination,
    setTablePagination,
    users,
    filters,
    setFilters,
    isFilterDirty,
    resetFilter,
  } satisfies IUsersContextType;
};
